import { ITherapy, Therapy, TherapyType } from '@alberta/konexi-shared';
import { therapyItems } from '@alberta/konexi-shared';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { flatten } from 'lodash';
import { Deferred } from 'src/app/common/deferred/deferred';
import { SelectionItem } from 'src/app/shared/models/selectionItem';
import { TherapyItem } from 'src/app/shared/models/therapy-item';

import { TenantConfigService } from './tenant-config.service';

export const TherapyItems = new InjectionToken<TherapyItem[]>('therapyItems', {
  providedIn: 'root',
  factory: () => therapyItems,
});

@Injectable({ providedIn: 'root' })
export class TherapyService {
  private _ready = new Deferred<void>();
  private _therapies: ITherapy[] = [];

  constructor(
    private _tenantConfigService: TenantConfigService,
    @Inject(TherapyItems) private _therapyItems: TherapyItem[]
  ) {
    void this._tenantConfigService.getTenantConfig().then(config => {
      if (config) {
        this._therapies = config.therapies;
      }
      this._ready.resolve();
    });
  }

  public async getTherapySelectionItems(): Promise<SelectionItem[]> {
    await this._ready.promise;

    return this._therapyItems
      .filter(therapyItem => this._therapies.some(therapy => therapy.therapy === therapyItem.id))
      .map(therapyItem => new SelectionItem(therapyItem.id, therapyItem.displayName))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  public async getTherapyTypSelectionItems(therapyNumber: number): Promise<SelectionItem[]> {
    await this._ready.promise;

    const therapy = this._therapies.find(therapy1 => therapy1.therapy === therapyNumber);
    if (!therapy) {
      return [];
    }

    const therapyItem = this._therapyItems.find(therapy1 => therapy1.id === therapyNumber);

    return therapyItem
      ? therapyItem.therapyTypes
          .filter(therapyTypeItem => therapy.therapyTypes.some(therapyType => therapyTypeItem.id === therapyType))
          .map(therapyTypeItem => new SelectionItem(therapyTypeItem.id, therapyTypeItem.displayName))
          .sort((a, b) => a.value.localeCompare(b.value))
      : [];
  }

  public async getTherapyFilterSelectionItems(): Promise<SelectionItem[]> {
    await this._ready.promise;

    return this._therapyItems
      .filter(therapyItem => this._therapies.some(therapy => therapy.therapy === therapyItem.id))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map(therapyItem => new SelectionItem(Therapy[therapyItem.id], therapyItem.displayName));
  }

  //TODO: Remove this once all lists use list.filter.component.ts instead of kenjo filters
  public async getTherapyFilterItems(): Promise<{ text: string; therapy: string }[]> {
    await this._ready.promise;

    return this._therapyItems
      .filter(therapyItem => this._therapies.some(therapy => therapy.therapy === therapyItem.id))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map(therapyItem => ({
        text: therapyItem.displayName,
        therapy: Therapy[therapyItem.id],
      }));
  }

  public async getTherapyTypeFilterItems(
    therapies?: string[] | undefined
  ): Promise<{ text: string; therapyType: string }[]> {
    await this._ready.promise;

    let customerTherapies = this._therapyItems.filter(therapyItem =>
      this._therapies.some(item => item.therapy === therapyItem.id)
    );

    if (therapies && therapies.length) {
      customerTherapies = customerTherapies.filter(item => therapies.includes(Therapy[item.id]));
    }

    return flatten(
      customerTherapies.map(therapyItem =>
        therapyItem.therapyTypes.filter(therapyTypeItem =>
          this._therapies
            .find(therapy => therapy.therapy === therapyItem.id)
            ?.therapyTypes?.some(therapyType => therapyTypeItem.id === therapyType)
        )
      )
    )
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map(item => ({ text: item.displayName, therapyType: TherapyType[item.id] }));
  }
}
